/**
 * Dashboard API Client
 * Provides functions to fetch KPI data for admin dashboard
 */

import { fetcher } from '@/config/api';

export interface MostActiveUser {
  rank: number;
  user_id: string;
  display_name: string;
  message_count: number;
}

export interface ModelUsage {
  rank: number;
  model_key: string;
  model_name: string;
  request_count: number;
}

export interface MessageTotals {
  received_messages: number;
  sent_messages: number;
  total_messages: number;
}

export interface UserMetrics {
  user_id: string;
  display_name: string;
  status: string;
  business_unit: string;
  total_messages: number;
  total_cost: number;
  tokens_input: number;
  tokens_output: number;
  most_used_model: string;
}

export interface ModelUsageDetails {
  model_key: string;
  model_name: string;
  message_count: number;
  tokens_input: number;
  tokens_output: number;
  cost: number;
}

export interface UserModelUsageDetails {
  'user_id': string;
  'display_name': string;
  'status': string;
  'business_unit': string;
  'total_messages': number;
  'total_cost': number;
  'tokens_input': number;
  'tokens_output': number;
  'date_range': {'start': string, 'end': string},
  'model_usage': ModelUsageDetails[];
}

export interface BusinessUnitCostAggregate {
  unit: string;
  cost: number;
}

export interface UserActivityAggregate {
  display_name: string;
  message_count: number;
}

export interface MonthlyTotalCost {
  month: string; // Format: 'mmm'
  cost: number;
}

export interface TTFTByModel {
  date: string;
  avg_ttft: number;
  min_ttft: number;
  max_ttft: number;
  count: number;
}

export interface DurationByAgent {
  date: string;
  avg_duration: number;
  min_duration: number;
  max_duration: number;
  count: number;
}

export interface DailyMessageActivity {
  date: string;
  total_messages: number;
  active_users: number;
  avg_messages_per_active_user: number;
}


/**
 * Get most active users by message count within date range
 */
export const getMostActiveUsers = async (
  startDate: string,
  endDate: string,
  limit: number = 5
): Promise<MostActiveUser[]> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    limit: limit.toString()
  });

  return fetcher<MostActiveUser[]>(`/api/admin/dashboard/most-active-users?${params}`);
};

/**
 * Get message totals (received from users vs sent by Bolt)
 */
export const getMessageTotals = async (
  startDate: string,
  endDate: string
): Promise<MessageTotals> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher<MessageTotals>(`/api/admin/dashboard/message-totals?${params}`);
};

/**
 * Get model usage statistics by request count within date range
 */
export const getModelUsage = async (
  startDate: string,
  endDate: string,
  limit: number = 5
): Promise<ModelUsage[]> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    limit: limit.toString()
  });

  return fetcher<ModelUsage[]>(`/api/admin/dashboard/model-usage?${params}`);
};

/**
 * Get all users with comprehensive metrics (messages, costs, model usage)
 * Respects role-based access control:
 * - Admins (role='admin' OR is_admin=True): See all users
 * - Managers: See their direct reports + themselves
 * - Regular users: See only themselves
 */
export const getUsersWithMetrics = async (
  startDate: string,
  endDate: string
): Promise<UserMetrics[]> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher<UserMetrics[]>(`/api/admin/dashboard/users-with-metrics?${params}`);
};

/**
 * Get detailed model usage for a specific user, including message counts, token usage, and costs per model
 */
export const getUserModelUsageDetails = async (
  userId: string,
  startDate: string,
  endDate: string
): Promise<UserModelUsageDetails> => {
  const params = new URLSearchParams({
    user_id: userId,
    start_date: startDate,
    end_date: endDate
  });

  return fetcher<UserModelUsageDetails>(`/api/admin/dashboard/user-model-usage-details?${params}`);
};


export const getMonthlyTotalCosts = async (
  requestingUserId: string,
  requestingUserRole: string,
  requestingUserIsAdmin: boolean,
  numMonths: number =12
): Promise<MonthlyTotalCost[]> => {
  const params = new URLSearchParams({
    requesting_user_id: requestingUserId,
    requesting_user_role: requestingUserRole,
    requesting_user_is_admin: requestingUserIsAdmin.toString(),
    num_months: numMonths.toString()
  });

  return fetcher<MonthlyTotalCost[]>(`/api/admin/dashboard/monthly-total-costs?${params}`);
}


/**
 * Get Time To First Token (TTFT) metrics aggregated by model
 */
export const getTTFTByModel = async (
  startDate: string,
  endDate: string
): Promise<{ start_date: string; end_date: string; metrics: TTFTByModel[]; count: number }> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher(`/api/admin/dashboard/ttft-by-model?${params}`);
};

/**
 * Get streaming duration metrics aggregated by agent
 */
export const getDurationByAgent = async (
  startDate: string,
  endDate: string
): Promise<{ start_date: string; end_date: string; metrics: DurationByAgent[]; count: number }> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher(`/api/admin/dashboard/duration-by-agent?${params}`);
};

/**
 * Get daily user message totals and average messages per active user.
 */
export const getDailyMessageActivity = async (
  startDate: string,
  endDate: string
): Promise<{ start_date: string; end_date: string; metrics: DailyMessageActivity[]; count: number }> => {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate
  });

  return fetcher(`/api/admin/dashboard/daily-message-activity?${params}`);
};



